import { Sparkles, Volume2, Film, RotateCcw } from 'lucide-react';
import type { GenerationStatus } from '@/types';
import { useVideoSaveAction } from '@/presentation/hooks/useVideoSaveAction';

export interface ActionButtonsProps {
  hasAllTexts: boolean;
  allAudioDone: boolean;
  hasScenes: boolean;
  hasVideoUrl: boolean;
  generationStatus: GenerationStatus;
  /** panel: 左パネルの縦積み / bar: スマートフォンの下部バー */
  variant?: 'panel' | 'bar';
  onGenerateAll: () => void;
  onGenerateAllAudio: () => void;
  onGenerateVideo: () => void;
  onSaveVideo: () => void;
  onReset: () => void;
}

export function ActionButtons({
  hasAllTexts,
  allAudioDone,
  hasScenes,
  hasVideoUrl,
  generationStatus,
  variant = 'panel',
  onGenerateAll,
  onGenerateAllAudio,
  onGenerateVideo,
  onSaveVideo,
  onReset,
}: ActionButtonsProps) {
  const isBar = variant === 'bar';
  // タッチ端末では共有シートで保存する。ラベルとアイコンもそれに合わせる (ADR-0010)
  const saveAction = useVideoSaveAction();

  // 有効・無効の条件は両方の見た目で同じものを使う
  const disableGenerateAll = !hasAllTexts || generationStatus.isGenerating;
  const disableGenerateVideo = !allAudioDone || generationStatus.isGenerating;
  const disableReset = !hasScenes || generationStatus.isGenerating;

  return (
    <div className={isBar ? 'space-y-2' : 'space-y-3'}>
      {/* Primary Action */}
      <button
        onClick={onGenerateAll}
        disabled={disableGenerateAll}
        className="btn-primary w-full justify-center"
      >
        <Sparkles size={18} />
        音声+動画生成
      </button>

      {isBar ? (
        /* 下部バー: 副次アクションを一行に詰める */
        <div className="mobile-action-bar-row flex gap-2">
          <button
            onClick={onGenerateAllAudio}
            disabled={disableGenerateAll}
            className="btn-secondary btn-compact flex-1 justify-center"
          >
            <Volume2 size={14} />
            音声のみ
          </button>
          <button
            onClick={onGenerateVideo}
            disabled={disableGenerateVideo}
            className="btn-tertiary btn-compact flex-1 justify-center"
          >
            <Film size={14} />
            動画のみ
          </button>
          {hasVideoUrl && (
            <button
              onClick={onSaveVideo}
              className="btn-accent btn-compact flex-1 justify-center"
            >
              <saveAction.Icon size={14} />
              {saveAction.label}
            </button>
          )}
          <button
            onClick={onReset}
            disabled={disableReset}
            className="btn-danger btn-compact flex-1 justify-center"
          >
            <RotateCcw size={14} />
            リセット
          </button>
        </div>
      ) : (
        <>
          {/* Secondary Actions */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={onGenerateAllAudio}
              disabled={disableGenerateAll}
              className="btn-secondary justify-center"
            >
              <Volume2 size={16} />
              音声のみ
            </button>
            <button
              onClick={onGenerateVideo}
              disabled={disableGenerateVideo}
              className="btn-tertiary justify-center"
            >
              <Film size={16} />
              動画のみ
            </button>
          </div>

          {/* Save & Reset */}
          <div className="flex gap-2">
            {hasVideoUrl && (
              <button
                onClick={onSaveVideo}
                className="btn-accent flex-1 justify-center"
              >
                <saveAction.Icon size={16} />
                {saveAction.label}
              </button>
            )}
            <button
              onClick={onReset}
              disabled={disableReset}
              className="btn-danger flex-1 justify-center"
            >
              <RotateCcw size={16} />
              リセット
            </button>
          </div>
        </>
      )}
    </div>
  );
}
